// LeadsOpportunityFeed — Section 3 of the Leads AI Command Center.
//
// A live-feed activity stream that mines recent events across every
// lead in view. Not a database read — surfaces signals the CRM
// already has (lead.activity[], outreach log entries, call log
// entries) and formats them as demand-generation moments.
//
// Renders inside LeadsView, above the view-mode pills, below the
// Morning Brief banner. Silence beats "nothing happened today" —
// hides itself if there's no notable activity.

import React from 'react';
import { useLeadIntelligence } from './LeadIntelligenceContext';

const DAY_MS = 86_400_000;

function fmtRelative(iso) {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const mins = Math.floor((Date.now() - t) / 60_000);
  if (mins < 1)   return 'just now';
  if (mins < 60)  return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// Convert raw activity entries into demand-shaped feed items.
// Every emit includes: id (dedup key), leadName, verb, subject,
// at, kind, prob (from intelligence context if available).
function extractFeed(leads, insightsByLeadId) {
  const items = [];
  const now = Date.now();
  const cutoff = now - 3 * DAY_MS;

  for (const lead of leads) {
    const activity = Array.isArray(lead.activity) ? lead.activity : [];
    const insight = insightsByLeadId?.[lead.id];
    for (const a of activity) {
      const at = Date.parse(a.at || a.created_at || 0);
      if (!Number.isFinite(at) || at < cutoff) continue;
      const type = String(a.type || '').toLowerCase();
      const body = String(a.body || '').trim();
      let verb = null;
      let kind = 'other';
      if (type === 'call' || /call/.test(body.toLowerCase())) { verb = 'called'; kind = 'call'; }
      else if (type === 'sms' || /text/.test(body.toLowerCase())) { verb = 'texted'; kind = 'sms'; }
      else if (type === 'email' || /email/.test(body.toLowerCase())) { verb = 'was emailed'; kind = 'email'; }
      else if (type === 'note') { verb = 'note added'; kind = 'note'; }
      else if (type === 'source') { verb = 'source changed'; kind = 'source'; }
      else if (type === 'policy') { verb = 'policy updated'; kind = 'policy'; }
      else if (type === 'appt') { verb = 'appointment set'; kind = 'appt'; }
      else if (body) { verb = body.slice(0, 60); kind = 'other'; }
      if (!verb) continue;
      items.push({
        id:     `${lead.id}::${a.at || a.created_at || ''}::${kind}`,
        leadId: lead.id,
        leadName: lead.name || '(unnamed lead)',
        verb, kind,
        at:      new Date(at).toISOString(),
        at_ms:   at,
        prob:    insight?.probability_pct || null,
        priority: insight?.priority_bucket || null,
      });
    }
  }

  // Sort newest first, dedup by id, cap 12.
  const seen = new Set();
  return items
    .sort((a, b) => b.at_ms - a.at_ms)
    .filter(i => { if (seen.has(i.id)) return false; seen.add(i.id); return true; })
    .slice(0, 12);
}

const KIND_STYLE = {
  call:   { icon: '☎️',  bg: '#eff6ff', text: '#1e40af' },
  sms:    { icon: '💬', bg: '#f0fdf4', text: '#166534' },
  email:  { icon: '✉️ ', bg: '#fef3c7', text: '#92400e' },
  note:   { icon: '📝', bg: '#f8fafc', text: '#334155' },
  appt:   { icon: '📅', bg: '#fef9c3', text: '#854d0e' },
  policy: { icon: '📄', bg: '#ecfdf5', text: '#065f46' },
  source: { icon: '🔗', bg: '#f1f5f9', text: '#475569' },
  other:  { icon: '·',  bg: '#f1f5f9', text: '#475569' },
};

export default function LeadsOpportunityFeed({ leads, onOpenLead }) {
  const intelligence = useLeadIntelligence();
  const insightsByLeadId = intelligence.data?.insights_by_lead_id || {};
  const items = React.useMemo(
    () => extractFeed(leads || [], insightsByLeadId),
    [leads, insightsByLeadId]
  );
  if (items.length === 0) return null;

  return (
    <div style={{
      background: '#ffffff',
      border: '1px solid #e2e8f0',
      borderRadius: 12,
      padding: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
        <div style={{ fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#0891b2', fontWeight: 800 }}>
          🔴 Opportunity feed
        </div>
        <div style={{ fontSize: 11, color: '#64748b' }}>
          Live activity across your book — last 72h
        </div>
      </div>
      <div style={{ display: 'grid', gap: 4 }}>
        {items.map(it => {
          const s = KIND_STYLE[it.kind] || KIND_STYLE.other;
          return (
            <button
              key={it.id}
              onClick={() => onOpenLead?.(it.leadId)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                background: 'transparent', border: `1px solid transparent`,
                padding: '6px 8px', borderRadius: 8, cursor: 'pointer',
                textAlign: 'left', width: '100%',
              }}
              className="hover:bg-slate-50"
            >
              <div style={{
                width: 26, height: 26, borderRadius: 999,
                background: s.bg, color: s.text,
                display: 'grid', placeItems: 'center', fontSize: 13, flexShrink: 0,
              }}>{s.icon}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: '#0f172a' }}>
                  <span style={{ fontWeight: 800 }}>{it.leadName}</span>
                  <span style={{ color: '#475569' }}> {it.verb}</span>
                  {it.prob != null && it.prob >= 50 && (
                    <span style={{
                      marginLeft: 6, fontSize: 10, fontWeight: 800, color: '#16a34a',
                      fontFamily: 'JetBrains Mono, monospace',
                    }}>{it.prob}% close</span>
                  )}
                </div>
                <div style={{ fontSize: 10, color: '#64748b' }}>{fmtRelative(it.at)}</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
